import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { createJSONEditor } from "vanilla-jsoneditor/standalone.js";

import toastError from "../../errors/toastError";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  page: {
    width: "100%",
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  logCard: {
    marginBottom: theme.spacing(2),
    overflow: "hidden",
  },
  logHeader: {
    padding: theme.spacing(1.5, 2),
    background: "#f7f9fc",
    borderBottom: "1px solid #e0e0e0",
  },
  columns: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  column: {
    minWidth: 0,
    border: "1px solid #e8edf3",
    borderRadius: 6,
    overflow: "hidden",
    background: "#fff",
  },
  columnTitle: {
    padding: theme.spacing(1),
    borderBottom: "1px solid #e8edf3",
    fontWeight: 700,
  },
  editor: {
    height: "calc(100vh - 310px)",
    minHeight: 420,
  },
  empty: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

const JsonViewer = ({ value }) => {
  const classes = useStyles();
  const targetRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    if (!targetRef.current) return undefined;

    editorRef.current = createJSONEditor({
      target: targetRef.current,
      props: {
        content: { json: value || {} },
        mode: "tree",
        mainMenuBar: true,
        navigationBar: true,
        statusBar: true,
        readOnly: true,
        truncateTextSize: 80,
      },
    });

    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.set({ json: value || {} });
  }, [value]);

  return <div className={classes.editor} ref={targetRef} />;
};

const formatUsage = log => {
  const parts = [];
  if (log.apiKeyMode) parts.push(log.apiKeyMode);
  if (log.model) parts.push(log.model);
  if (log.totalTokens) parts.push(`${log.totalTokens} tokens`);
  if (log.costUsd) parts.push(`$${Number(log.costUsd).toFixed(6)}`);
  if (log.creditCost) parts.push(`${Number(log.creditCost).toFixed(6)} creditos`);
  return parts.join(" - ");
};

const AiApiLogsPage = () => {
  const classes = useStyles();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/ai-api-logs");
      setLogs(data.logs || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteLogs = async () => {
    if (!window.confirm("Eliminar todos los registros de OpenAI?")) return;

    setLoading(true);
    try {
      await api.delete("/settings/ai-api-logs");
      setLogs([]);
      toast.success("Registros eliminados.");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <div>
          <Typography variant="h6">Logs de OpenAI</Typography>
          <Typography variant="body2" color="textSecondary">
            Ultimas 5 peticiones salientes, respuestas recibidas e hilo de conversacion.
          </Typography>
        </div>
        <div className={classes.actions}>
          {loading && <CircularProgress size={20} />}
          <Button variant="outlined" color="primary" onClick={loadLogs}>
            Refrescar
          </Button>
          <Button variant="outlined" color="secondary" onClick={deleteLogs}>
            Eliminar todos los registros
          </Button>
        </div>
      </div>

      {!logs.length && !loading && (
        <Paper className={classes.empty}>Sin peticiones registradas.</Paper>
      )}

      {logs.map(log => (
        <Paper className={classes.logCard} key={log.id}>
          <div className={classes.logHeader}>
            <Typography variant="body2">
              {log.method || "POST"} {log.endpoint}
              {log.statusCode ? ` - ${log.statusCode}` : ""}
              {log.durationMs ? ` - ${log.durationMs} ms` : ""}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {new Date(log.createdAt).toLocaleString()}
              {log.ticketId ? ` - Ticket #${log.ticketId}` : ""}
              {log.contact ? ` - ${log.contact.name || log.contact.number}` : ""}
              {formatUsage(log) ? ` - ${formatUsage(log)}` : ""}
            </Typography>
          </div>

          <div className={classes.columns}>
            <div className={classes.column}>
              <Typography className={classes.columnTitle} variant="body2">
                Enviado
              </Typography>
              <JsonViewer value={log.request || {}} />
            </div>
            <div className={classes.column}>
              <Typography className={classes.columnTitle} variant="body2">
                Recibido
              </Typography>
              <JsonViewer value={log.response || (log.error ? { error: log.error } : {})} />
            </div>
            <div className={classes.column}>
              <Typography className={classes.columnTitle} variant="body2">
                Hilo de conversacion
              </Typography>
              <JsonViewer value={log.thread || {}} />
            </div>
          </div>
        </Paper>
      ))}
    </div>
  );
};

export default AiApiLogsPage;
